// src/router/index.js

import { createRouter, createWebHistory } from 'vue-router/auto'
import { useAuthStore } from '@/store/auth'
import { useSegment } from '@/composables/analytics/useSegment'
import { useGeneralStore } from '@/store/general'
import { useFilterStore } from '@/store/filter'
import { log } from '@/plugins/logger'
import { isSessionExpired } from '@/utils/sessionTimeout'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL)
})

router.beforeEach(async (to, from, next) => {
  try {
    const authStore = useAuthStore()

    // Authentication and Authorization Checks

    // Check session expiry for authenticated users
    if (authStore.user && isSessionExpired()) {
      await authStore.logoutUser()
      return next({ path: '/login', query: { redirect: to.fullPath } })
    }

    if (to.path !== '/login' && !authStore.user) {
      // User is not authenticated, redirect to login
      return next({ path: '/login', query: { redirect: to.fullPath } })
    }

    if (authStore.user && authStore.userIsDisabled) {
      if (to.path === '/account-paused' || to.path === '/login') return next()
      return next({ path: '/account-paused' })
    }

    if (to.path === '/account-paused') {
      return next({ path: '/' })
    }

    if (to.path === '/login' && authStore.user) {
      // Authenticated user trying to access login, redirect to home
      return next({ path: '/' })
    }

    next()
  } catch (error) {
    log.error('Error during navigation guard:', error)
    next({ path: '/sorry', query: { message: 'Navigation Error' } })
  }
})

router.afterEach((to, from) => {
  const generalStore = useGeneralStore()
  const filterStore = useFilterStore()

  if (!from.path.includes('accounts')) {
    generalStore.clearAccountDisplayCache()
  }

  // Clear filters when navigating between pages
  filterStore.clearFilter()

  // Track page view after navigation is complete
  const authStore = useAuthStore()
  const segment = useSegment()

  if (authStore.user) {
    segment.trackPage(to.path)

    // Track login source if applicable
    const hasSource = to.query.source
    if (hasSource) {
      segment.trackLoginSource(hasSource, to.fullPath)
    }
  }
})

export default router
